import React, { useEffect, useState, useRef } from 'react';
import './index.scss';
import classNames from 'classnames';

interface HourglassButtonProps {
    active?: boolean;            // アクティブ状態かどうか
    onAnimationComplete?: () => void;  // アニメーション完了時のコールバック
}

export default function HourglassButton({ active = false, onAnimationComplete }: HourglassButtonProps) {
    const [animationState, setAnimationState] = useState({
        isRotating: false,
        isFlipped: false,
        showAnimation: false,
        pausedAnimation: false
    });
    const prevActiveRef = useRef(active);
    const sandTopRef = useRef<HTMLDivElement>(null);
    const sandBottomRef = useRef<HTMLDivElement>(null);

    // active の変化を監視
    useEffect(() => {
        // active が変化したときのみアニメーションをトリガー
        if (prevActiveRef.current !== active) {
            prevActiveRef.current = active;
            const wasShowingAnimation = animationState.showAnimation;
            if (wasShowingAnimation) {
                if (sandTopRef.current && sandBottomRef.current) {
                    // 計算後のスタイルを取得
                    const topStyle = window.getComputedStyle(sandTopRef.current);
                    const bottomStyle = window.getComputedStyle(sandBottomRef.current);

                    // clip-path の形式を修正し、完全な (top right bottom left) 形式にする
                    const topClipPath = topStyle.clipPath;
                    const bottomClipPath = bottomStyle.clipPath;

                    // デバッグ情報を出力
                    console.log('検出された clipPath 値:', { top: topClipPath, bottom: bottomClipPath });

                    // 不足しているパラメータがあれば追加
                    let fixedTopClipPath = topClipPath;
                    let fixedBottomClipPath = bottomClipPath;

                    // inset 形式の場合、不足パラメータの追加をチェック
                    if (topClipPath.startsWith('inset(') && !topClipPath.includes('0px)') && !topClipPath.includes('0)')) {
                        fixedTopClipPath = topClipPath.replace(')', ' 0px)');
                    }
                    if (bottomClipPath.startsWith('inset(') && !bottomClipPath.includes('0px)') && !bottomClipPath.includes('0)')) {
                        fixedBottomClipPath = bottomClipPath.replace(')', ' 0px)');
                    }

                    // CSS変数ではなく直接スタイルに設定
                    sandTopRef.current.style.setProperty('--current-clip-path-value', fixedTopClipPath);
                    sandBottomRef.current.style.setProperty('--current-clip-path-value', fixedBottomClipPath);

                    // より柔軟な正規表現で、より多くの形式に対応
                    const match = /inset\(\s*([\d.]+)(?:px)?\s+([\d.]+)(?:px)?\s+([\d.]+)(?:px)?\s+([\d.]+)(?:px)?\s*\)/.exec(fixedTopClipPath);
                    if (match && match[3]) { // ここでは match[3] を使用します。3番目のキャプチャグループが下部の値だからです
                        const bottomValue = match[3];
                        console.log('抽出された下部クリップ値:', bottomValue, 'px');
                        // 下部クリップ値変数を設定
                        sandTopRef.current.style.setProperty('--current-bottom-value', `${bottomValue}px`);
                    } else {
                        // 他の形式の場合を確認
                        console.log('抽出できませんでした。デフォルト値45pxを使用します');
                        console.log('現在の clip-path 形式:', fixedTopClipPath);
                        sandTopRef.current.style.setProperty('--current-bottom-value', '45px');
                    }
                    // 下部要素のクリップ値を抽出
                    const bottomMatch = /inset\(\s*([\d.]+)(?:px)?\s+([\d.]+)(?:px)?\s+([\d.]+)(?:px)?\s+([\d.]+)(?:px)?\s*\)/.exec(fixedBottomClipPath);
                    if (bottomMatch && bottomMatch[3]) { // 3番目のキャプチャグループが下部の値です
                        const bottomValueForBottom = bottomMatch[3];
                        console.log('抽出された下部要素の下部クリップ値:', bottomValueForBottom, 'px');
                        // 下部要素の下部クリップ値変数を設定
                        sandBottomRef.current.style.setProperty('--current-bottom-value-bottom', `${bottomValueForBottom}px`);
                    } else {
                        // 抽出できない場合はデフォルト値を使用
                        console.log('抽出できませんでした。デフォルト値30pxを使用します');
                        console.log('現在の下部 clip-path 形式:', fixedBottomClipPath);
                        sandBottomRef.current.style.setProperty('--current-bottom-value-bottom', '30px');
                    }
                }
            }

            // ステップ1: アニメーションを停止して回転を開始
            setAnimationState({
                isRotating: true,
                isFlipped: animationState.isFlipped,
                showAnimation: false,
                pausedAnimation: wasShowingAnimation
            });

            // ステップ2: 回転完了後に反転状態を切り替え、active に応じてアニメーションを表示
            setTimeout(() => {
                setAnimationState(prev => ({
                    isRotating: false,
                    isFlipped: !prev.isFlipped,  // 反転状態を切り替え
                    showAnimation: active,       // active=true の場合のみアニメーションを表示
                    pausedAnimation: wasShowingAnimation
                }));

                const resetTimer = setTimeout(() => {
                    setAnimationState(prev => ({
                        ...prev,
                        pausedAnimation: false
                    }));
                }, 800);
                return () => clearTimeout(resetTimer);
            }, 1000);
        }
    }, [active, onAnimationComplete, animationState.isFlipped, animationState.showAnimation]);

    useEffect(() => {
        // showAnimation かつ active 状態の場合にのみタイマーをセット
        if (animationState.showAnimation && active) {
            console.log('アニメーション開始、17.5秒のタイマーを設定');

            const animationTimer = setTimeout(() => {
                console.log('砂時計アニメーション完了');
                if (onAnimationComplete) {
                    onAnimationComplete();
                }
            }, 17500); // CSSアニメーションの長さと一致

            return () => {
                console.log('アニメーションタイマーをクリア');
                clearTimeout(animationTimer);
            };
        }
    }, [animationState.showAnimation, active, onAnimationComplete]);

    const hourglassClasses = classNames("hourglass", {
        'rotating': animationState.isRotating,
        'active': active,
        'show-animation': animationState.showAnimation,
        'flipped': animationState.isFlipped,
        'paused-animation': animationState.pausedAnimation
    });

    return (
        <div className={hourglassClasses}>
            <div ref={sandTopRef} className={classNames("sand top", { 'hidden': !active && !animationState.isRotating })} />
            <div className={classNames("sand middle", { 'hidden': !active && !animationState.isRotating })} />
            <div ref={sandBottomRef} className="sand bottom" />
            <img className="frame" src="/uploads/default/hourglass.png" alt="hourglass" />
        </div>
    );
}